<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="detailedForm">
      <!-- Description Row -->
      <v-row>
        <v-col cols="12">
          <label>{{ $t('description') }}</label>
          <p class="label-info">
            {{ $t('equipment-detailed-description') }}
          </p>
          <tiptap-vuetify
            v-model="equipment.description"
            :placeholder="'Max length is 2000 characters'"
            :extensions="extensions"
            class="required"
            :rules="requiredRule()"
          ></tiptap-vuetify>
          <div :class="charCount > maxChars ? 'red--text' : 'black--text'">
            {{ charCount }} / {{ maxChars }} characters
          </div>
        </v-col>
      </v-row>

      <!-- Tagline Row -->
      <v-row class="pt-0 mt-0">
        <v-col cols="12" sm="12" md="4">
          <label>{{ $t('tagline') }}</label>
          <p class="label-info">
            {{ $t('equipment-tagline-info') }}
          </p>
          <v-text-field
            v-model.trim="equipment.tagline"
            outlined
            :rules="requiredRule()"
            class="required"
            :placeholder="'TagLine'"
            clearable
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Use Cases Row -->
      <v-row
        v-for="(useCase, index) in equipment.useCases"
        :key="index"
        align="center"
        class="mt-0 pt-0"
      >
        <v-col cols="9" sm="9" md="4">
          <label>{{ $t('use-case') }}</label>
          <p class="label-info">
            {{ $t('equipment-user-cases-info') }}
          </p>
          <v-text-field
            v-model.trim="equipment.useCases[index]"
            outlined
            :rules="[...requiredRule(), ...minMaxLengthRule(4, 500)]"
            class="required"
            :placeholder="'Use Case'"
            clearable
          ></v-text-field>
        </v-col>
        <v-col class="mt-5" v-if="equipment.useCases.length > 1">
          <v-btn fab small text @click="removeUseCase(index)">
            <v-icon>
              $vuetify.icons.trash
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Conditionally render the Add Use Case button -->
      <v-row v-if="hasNonEmptyUseCases" class="mt-0 pt-0">
        <v-col cols="4">
          <v-btn
            @click="addUseCase"
            depressed
            rounded
            large
            color="primary200"
            class="white--text font-weight-bold"
          >
            <v-icon left>
              $vuetify.icons.plus
            </v-icon>
            <span class="text-decoration-underline text-transform-none font-weight-bold">
              {{ $t('add-use-case') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Optional Detailed Info for Edit -->
      <v-row v-if="edit">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="mt-5">
                <!-- Logo & Picture -->
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>Logo</label>
                    <p class="label-info fixed-label-height">
                      Link to the logo/visual identity of the Equipment. The loo will be visible at
                      the Portal. If there is no specific logo for the Services the logo of the
                      Provider may be used.
                    </p>
                    <v-text-field
                      v-model.trim="optionalEquipment.logo"
                      outlined
                      :placeholder="'Paste a URL'"
                      prepend-inner-icon="mdi-link-variant"
                      clearable
                      @input="handleLogoInput"
                    >
                    </v-text-field>
                    <v-alert v-if="!isValidLogoUrl" type="error" class="subtitle-2 py-2">
                      Please enter a valid URL
                    </v-alert>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>Picture</label>
                    <p class="label-info fixed-label-height">
                      Link to the picture identity of the Equipment. <br />
                      The picture will be visible at the Portal.
                    </p>
                    <v-text-field
                      v-model.trim="optionalEquipment.picture"
                      outlined
                      :placeholder="'Paste a URL'"
                      prepend-inner-icon="mdi-link-variant"
                      clearable
                      @input="handlePictureInput"
                    >
                    </v-text-field>
                    <v-alert v-if="!isValidPictureUrl" type="error" class="subtitle-2 py-2">
                      Please enter a valid URL
                    </v-alert>
                  </v-col>
                </v-row>

                <!-- Related Resources -->
                <v-row class="mt-0">
                  <v-col cols="12" sm="12" md="12">
                    <label>Related Resources</label>
                    <p class="label-info">
                      Select any related resources.
                    </p>
                    <input-autocomplete-search
                      v-model="optionalEquipment.relatedResources"
                      multiple
                      chips
                      label="Select any related resources"
                      prepend-inner-icon="mdi-magnify"
                      clearable
                      return-object
                      outlined
                      :item-value="'id'"
                      :item-text="'name'"
                      :url="'/api/v1/resources/by-name?name='"
                    ></input-autocomplete-search>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          :disabled="charCount > maxChars"
          v-if="unsavedChanges && edit"
          @click="updateEquipmentDetailedInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="black"
          @click="back"
          outlined
          class="black--text font-weight-bold mx-2"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn
          :disabled="charCount > maxChars"
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
          >{{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import InputAutocompleteSearch from '@/components/shared/inputs/InputAutocompleteSearch';
import {
  TiptapVuetify,
  Bold,
  Italic,
  Underline,
  Strike,
  ListItem,
  BulletList,
  OrderedList,
} from 'tiptap-vuetify';

export default {
  name: 'EquipmentDetails',
  mixins: [util],
  components: { TiptapVuetify, AlertUnsavedChanges, InputAutocompleteSearch },
  data: () => ({
    equipment: {
      description: '',
      tagline: '',
      useCases: [''],
    },
    optionalEquipment: {
      logo: '',
      picture: '',
      relatedResources: [],
    },
    initialEquipment: {},
    initialOptionalEquipment: {},
    status: false,
    initialStatus: {},
    isValidLogoUrl: true,
    isValidPictureUrl: true,
    dataLoaded: false,
    unsavedChanges: false,
    extensions: [Bold, Italic, Underline, Strike, ListItem, BulletList, OrderedList],
    maxChars: 2000,
  }),
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    published: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    equipment: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;

        const descriptionChanged = this.equipment.description !== this.initialEquipment.description;
        const taglineChanged = this.equipment.tagline !== this.initialEquipment.tagline;

        const useCasesChanged =
          JSON.stringify(this.equipment.useCases) !==
          JSON.stringify(this.initialEquipment.useCases);

        this.unsavedChanges = descriptionChanged || taglineChanged || useCasesChanged;
      },
    },
    optionalEquipment: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;

        const logoChanged = this.optionalEquipment.logo !== this.initialOptionalEquipment.logo;
        const pictureChanged =
          this.optionalEquipment.picture !== this.initialOptionalEquipment.picture;
        const relatedResourcesChanged =
          JSON.stringify(this.optionalEquipment.relatedResources) !==
          JSON.stringify(this.initialOptionalEquipment.relatedResources);

        this.unsavedChanges = logoChanged || pictureChanged || relatedResourcesChanged;
      },
    },
    published: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges = this.status !== this.published;
      },
    },
  },
  mounted() {
    if (this.edit) {
      this.getEquipmentIdDetailedInfo();
    }
  },
  computed: {
    hasNonEmptyUseCases() {
      return (
        this.equipment.useCases.length === 0 ||
        this.equipment.useCases.some(useCase => useCase.trim() !== '')
      );
    },
    charCount() {
      const div = document.createElement('div');
      div.innerHTML = this.equipment.description || '';
      return div.textContent.length;
    },
  },
  methods: {
    next() {
      const isFormValid = this.$refs.detailedForm.validate();
      if (isFormValid && this.equipment.description) {
        this.$emit('tab', 2);
        this.$emit('equipmentDetailsInfo', { ...this.equipment });
      }
    },
    back() {
      this.$emit('tab', 0);
    },
    addUseCase() {
      this.equipment.useCases.push('');
    },
    removeUseCase(index) {
      this.equipment.useCases.splice(index, 1);
    },
    getEquipmentIdDetailedInfo() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get(`/api/v1/equipment/${id}`)
        .then(response => {
          console.log('Get Equipment Detailed Info', response.data);

          this.status = response.data.published;
          this.equipment.description = response.data.description;
          this.equipment.tagline = response.data.tagline;
          this.equipment.useCases = response.data.useCases;
          this.optionalEquipment.logo = response.data.logo;
          this.optionalEquipment.picture = response.data.picture;
          this.optionalEquipment.relatedResources = response.data.relatedResources;

          this.initialEquipment = {
            description: response.data.description,
            tagline: response.data.tagline,
            useCases: [...response.data.useCases],
          };

          this.initialOptionalEquipment = {
            logo: response.data.logo,
            picture: response.data.picture,
            relatedResources: [...response.data.relatedResources],
          };
          this.initialStatus = {
            published: response.data.published,
          };
          this.$emit('status', this.initialStatus.published);
          this.dataLoaded = true;
        })
        .catch(error => {
          console.error('Error fetching equipment data:', error);
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    updateEquipmentDetailedInfo() {
      const id = this.$route.params.id;
      const equipmentUpdateData = {
        published: this.published,
        id: id,
        tagline: this.equipment.tagline,
        useCases: [...this.equipment.useCases],
        description: this.equipment.description,
        logo: this.optionalEquipment.logo,
        picture: this.optionalEquipment.picture,
        relatedResources: [...this.optionalEquipment.relatedResources],
      };

      this.deleteEmptyProperties(equipmentUpdateData);

      const isFormValid = this.$refs.detailedForm.validate();
      if (isFormValid) {
        this.axios
          .put('api/v1/equipment/detailed-info', equipmentUpdateData)
          .then(response => {
            console.log('Equipment info updated successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Equipment Updated Successfully',
            });
            this.initialEquipment = { ...this.equipment };
            this.initialOptionalEquipment = { ...this.optionalEquipment };
            this.initialStatus = this.status;
            this.unsavedChanges = false;
            this.$emit('updatedChanges', true);

            this.getEquipmentIdDetailedInfo();
          })
          .catch(error => {
            console.error('Error updating equipment info:', error);
          });
      }
    },
    cancel() {
      this.getEquipmentIdDetailedInfo();
    },
    deleteEmptyProperties(obj) {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (item === null || item === undefined || item === '') {
            obj.splice(index, 1);
          } else if (typeof item === 'object') {
            this.deleteEmptyProperties(item);
            if (Object.keys(item).length === 0) {
              obj.splice(index, 1);
            }
          }
        });
      } else {
        for (const key in obj) {
          if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
            delete obj[key];
          } else if (Array.isArray(obj[key])) {
            this.deleteEmptyProperties(obj[key]);
            if (obj[key].length === 0) {
              delete obj[key];
            }
          } else if (typeof obj[key] === 'object') {
            this.deleteEmptyProperties(obj[key]);
            if (Object.keys(obj[key]).length === 0) {
              delete obj[key];
            }
          }
        }
      }
    },
    handleLogoInput(value) {
      if (value) {
        this.validateLogoUrl(value);
      } else {
        this.isValidLogoUrl = true;
      }
    },
    validateLogoUrl() {
      this.isValidLogoUrl = this.canParse(this.optionalEquipment.logo);
    },
    handlePictureInput(value) {
      if (value) {
        this.validatePictureUrl(value);
      } else {
        this.isValidPictureUrl = true;
      }
    },
    validatePictureUrl() {
      this.isValidPictureUrl = this.canParse(this.optionalEquipment.picture);
    },
    canParse(link) {
      if (!/^https?:\/\//i.test(link)) {
        return false;
      }
      try {
        new URL(link);
        return true;
      } catch (e) {
        return false;
      }
    },
  },
};
</script>
